import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AuditKayit, GunlukSatir, KasaOzeti, Kullanici, YedekDosya, YedekDurumRaporu, YedekDurumu, YedekKosusu,
} from '@nero/shared';
import {
  ARALIK_ADI, DOVIZ_KODLARI, ROLLER, ROL_ADI, araligiCoz, bugun, boyutGoster, para,
  paraBirimiSimgesi, tarihGoster, tarihSaatGoster,
} from '@nero/shared';
import type { AralikAnahtari } from '@nero/shared';
import { api, useEylem, useVeri } from '../lib/veri.js';
import { kuruTazele, type KurDurumu } from '../lib/kur.js';
import { useBildirim } from '../lib/bildirim.js';
import { oturumBasligi, sorgu } from '../lib/api.js';
import { BosDurum, HataKutusu, Kart, KartBaslik, Olcum, Rozet, SayfaBaslik, UyariSerit, Yukleniyor } from '../bilesenler/temel.js';
import { Girdi, MetinAlani, Onay, ParaGirdi, Secim, SecenekSerit } from '../bilesenler/form.js';
import { OnayPenceresi, Pencere } from '../bilesenler/Pencere.js';
import { Simge } from '../bilesenler/Simge.js';
import { NeroLogo } from '../bilesenler/Logo.js';

type Sekme = 'rapor' | 'firma' | 'kullanici' | 'yedek' | 'audit' | 'cop';

/** Eski kayıtlarda yazım düzeltmesi sonucu (sunucudan gelir). */
interface YazimDuzeltmeSonucu {
  kuru: boolean;
  incelenen: number;
  degisen: number;
  ornekler: Array<{ tablo: string; etiket: string; id: number; onceki: string; sonraki: string }>;
  ozet: Record<string, number>;
  yedek?: string;
}

export function Yonetici() {
  const [sekme, setSekme] = useState<Sekme>('rapor');
  return (
    <div className="sayfa">
      <SayfaBaslik baslik="Yönetici" alt="Finans, kullanıcılar, yedekleme ve işlem geçmişi" />
      <div style={{ marginBottom: 'var(--b5)' }}>
        <SecenekSerit
          deger={sekme}
          secenekler={[
            { deger: 'rapor', ad: 'Raporlar' },
            { deger: 'firma', ad: 'Firma' },
            { deger: 'kullanici', ad: 'Kullanıcılar' },
            { deger: 'yedek', ad: 'Yedekleme' },
            { deger: 'audit', ad: 'İşlem geçmişi' },
            { deger: 'cop', ad: 'Çöp kutusu' },
          ]}
          onChange={setSekme}
        />
      </div>
      {sekme === 'rapor' && <Raporlar />}
      {sekme === 'firma' && <Firma />}
      {sekme === 'kullanici' && <Kullanicilar />}
      {sekme === 'yedek' && <Yedekleme />}
      {sekme === 'audit' && <Audit />}
      {sekme === 'cop' && <Cop />}
    </div>
  );
}

/* ---------- Raporlar ---------- */

/* Rapor aralıkları kasa ile aynı tanımı kullanır: tek yerde tutulur. */
const RAPOR_ARALIKLARI: AralikAnahtari[] = ['bugun', 'son_7', 'bu_ay', 'gecen_ay', 'aralik'];

interface RaporVeri {
  ozet: KasaOzeti;
  gunluk: GunlukSatir[];
  odemeTipleri: Array<{ odeme_tipi: string; toplam: number; adet: number }>;
  enCokHizmetler: Array<{ ad: string; adet: number; toplam: number }>;
  enCokUrunler: Array<{ ad: string; adet: number; toplam: number }>;
  isEmriOzeti: { acik: number; bugun: number; tamamlanan: number; ciro: number; tahsilat: number; bekleyenTahsilat: number };
}

function Raporlar() {
  const bildir = useBildirim();
  const [on, setOn] = useState<AralikAnahtari>('bu_ay');
  const [bas, setBas] = useState(() => bugun());
  const [bit, setBit] = useState(() => bugun());
  const [ciktiGorunumu, setCiktiGorunumu] = useState(false);

  const aralik = useMemo(() => araligiCoz(on, bugun(), bas, bit), [on, bas, bit]);

  /* Ön ayar seçilince özel alanlar da o aralığa taşınır: kullanıcı
     "Geçen ay"dan "Tarih aralığı"na geçtiğinde kutular boş kalmaz. */
  useEffect(() => {
    if (on === 'aralik') return;
    const a = araligiCoz(on);
    if (a.baslangic) setBas(a.baslangic);
    if (a.bitis) setBit(a.bitis);
  }, [on]);

  const { veri, yukleniyor, hata, yenile } = useVeri<RaporVeri>(
    `/raporlar${sorgu({ baslangic: aralik.baslangic, bitis: aralik.bitis })}`,
  );

  /**
   * GERÇEK PDF indirme. Tarayıcının yazdır penceresi AÇILMAZ: dosya sunucuda
   * üretilir ve doğrudan iner. Sunucuya yalnızca tarih aralığı gider;
   * bütün toplamlar orada yeniden hesaplanır.
   */
  const pdfIndir = useEylem(async () => {
    const q = sorgu({ baslangic: aralik.baslangic, bitis: aralik.bitis, aralik: on });
    const yanit = await fetch(`/api/raporlar/pdf${q}`, {
      credentials: 'same-origin', headers: oturumBasligi(),
    });
    if (!yanit.ok) throw new Error('Rapor PDF üretilemedi. Sunucu günlüğünü kontrol edin.');
    const blob = await yanit.blob();
    const adBasligi = yanit.headers.get('content-disposition') ?? '';
    const eslesme = /filename="([^"]+)"/.exec(adBasligi);
    const url = URL.createObjectURL(blob);
    const ba = document.createElement('a');
    ba.href = url;
    ba.download = eslesme?.[1] ?? 'Nero-Rapor.pdf';
    document.body.appendChild(ba);
    ba.click();
    ba.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    bildir('Rapor PDF indirildi');
  });
  if (hata) return <HataKutusu hata={hata} yenile={yenile} />;
  if (yukleniyor || !veri) return <Yukleniyor satir={5} />;

  const enYuksek = Math.max(1, ...veri.gunluk.map((g) => Math.max(g.girdi, g.cikti)));
  const baslik = ARALIK_ADI[on];

  if (ciktiGorunumu) {
    return (
      <div>
        <div className="yazdirma-gizle satir" style={{ marginBottom: 'var(--b4)' }}>
          <button className="btn btn-sade" onClick={() => setCiktiGorunumu(false)}>
            <Simge ad="ok-sol" boyut={14} /> Rapor ekranına dön
          </button>
          <button className="btn btn-sade" onClick={() => window.print()}>
            <Simge ad="dosya" className="simge" /> Yazdır
          </button>
          <button className="btn btn-birincil" onClick={() => void pdfIndir.yurut(undefined as never)}
            disabled={pdfIndir.calisiyor}>
            <Simge ad="indir" className="simge" />
            {pdfIndir.calisiyor ? 'Hazırlanıyor…' : 'PDF indir'}
          </button>
        </div>
        {pdfIndir.hata && (
          <div className="yazdirma-gizle" style={{ marginBottom: 'var(--b4)' }}>
            <UyariSerit ton="kritik">{pdfIndir.hata}</UyariSerit>
          </div>
        )}
        <RaporBelgesi veri={veri} aralik={aralik} baslik={baslik} />
      </div>
    );
  }

  return (
    <div className="sutun" style={{ gap: 'var(--b5)' }}>
      <div className="satir" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--b3)' }}>
        <SecenekSerit
          deger={on}
          secenekler={RAPOR_ARALIKLARI.map((a) => ({ deger: a, ad: ARALIK_ADI[a] }))}
          onChange={setOn}
        />
        <div className="satir">
          <button className="btn btn-sade" onClick={() => setCiktiGorunumu(true)}>
            <Simge ad="dosya" className="simge" /> Rapor çıktısı
          </button>
          <button className="btn btn-ikincil" onClick={() => void pdfIndir.yurut(undefined as never)}
            disabled={pdfIndir.calisiyor}>
            <Simge ad="indir" className="simge" />
            {pdfIndir.calisiyor ? 'Hazırlanıyor…' : 'PDF indir'}
          </button>
        </div>
      </div>

      {on === 'aralik' && (
        <div className="satir" style={{ gap: 'var(--b3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ width: 180 }}>
            <Girdi etiket="Başlangıç" type="date" value={bas} onChange={setBas} max={bit} />
          </div>
          <div style={{ width: 180 }}>
            <Girdi etiket="Bitiş" type="date" value={bit} onChange={setBit} min={bas} />
          </div>
        </div>
      )}

      <div style={{ fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
        {tarihGoster(aralik.baslangic)} – {tarihGoster(aralik.bitis)}
      </div>

      <div className="izgara izgara-4">
        <Olcum etiket="Toplam girdi" deger={para(veri.ozet.girdi)} vurgu="olumlu" alt={`${veri.ozet.adet} işlem`} />
        <Olcum etiket="Toplam çıktı" deger={para(veri.ozet.cikti)} />
        <Olcum etiket="Net" deger={para(veri.ozet.net)} buyuk vurgu={veri.ozet.net >= 0 ? 'olumlu' : 'uyari'} />
        <Olcum etiket="Bekleyen tahsilat" deger={para(veri.isEmriOzeti.bekleyenTahsilat)}
          vurgu={veri.isEmriOzeti.bekleyenTahsilat > 0 ? 'uyari' : undefined}
          alt={`${veri.isEmriOzeti.tamamlanan} iş tamamlandı`} />
      </div>

      <Kart>
        <KartBaslik baslik="Günlük döküm" />
        {veri.gunluk.length === 0 ? (
          <BosDurum simge="grafik" baslik="Bu dönemde veri yok" />
        ) : (
          <div className="kart-govde sutun" style={{ gap: 'var(--b2)' }}>
            {veri.gunluk.map((g) => (
              <div key={g.tarih} className="satir" style={{ gap: 'var(--b3)' }}>
                <span className="ikincil sayi" style={{ width: 84, fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
                  {tarihGoster(g.tarih)}
                </span>
                <div className="esnek" style={{ display: 'flex', gap: 2, height: 20, alignItems: 'center' }}>
                  <div style={{
                    width: `${(g.girdi / enYuksek) * 100}%`, height: 8,
                    background: 'var(--olumlu)', borderRadius: 2, minWidth: g.girdi ? 2 : 0,
                  }} />
                  {g.cikti > 0 && (
                    <div style={{
                      width: `${(g.cikti / enYuksek) * 100}%`, height: 8,
                      background: 'var(--uyari)', borderRadius: 2, minWidth: 2,
                    }} />
                  )}
                </div>
                <span className="para" style={{ width: 120, textAlign: 'right', fontSize: 'var(--p-12)' }}>
                  {para(g.girdi - g.cikti)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Kart>

      <div className="izgara izgara-3">
        <Kart>
          <KartBaslik baslik="Ödeme yöntemi" />
          <div className="kart-govde sutun">
            {veri.odemeTipleri.length === 0 ? <span className="ikincil">Kayıt yok</span>
              : veri.odemeTipleri.map((o) => (
                <div key={o.odeme_tipi} className="satir-arasi">
                  <span>{o.odeme_tipi} <span style={{ color: 'var(--metin-3)' }}>· {o.adet}</span></span>
                  <span className="para">{para(o.toplam)}</span>
                </div>
              ))}
          </div>
        </Kart>
        <Kart>
          <KartBaslik baslik="En çok yapılan işlem" />
          <div className="kart-govde sutun">
            {veri.enCokHizmetler.length === 0 ? <span className="ikincil">Kayıt yok</span>
              : veri.enCokHizmetler.slice(0, 8).map((h) => (
                <div key={h.ad} className="satir-arasi">
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {h.ad} <span style={{ color: 'var(--metin-3)' }}>· {h.adet}</span>
                  </span>
                  <span className="para">{para(h.toplam)}</span>
                </div>
              ))}
          </div>
        </Kart>
        <Kart>
          <KartBaslik baslik="En çok satılan ürün" />
          <div className="kart-govde sutun">
            {veri.enCokUrunler.length === 0 ? <span className="ikincil">Kayıt yok</span>
              : veri.enCokUrunler.slice(0, 8).map((u) => (
                <div key={u.ad} className="satir-arasi">
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.ad} <span style={{ color: 'var(--metin-3)' }}>· {u.adet}</span>
                  </span>
                  <span className="para">{para(u.toplam)}</span>
                </div>
              ))}
          </div>
        </Kart>
      </div>
    </div>
  );
}

/** Yazdırılabilir A4 rapor. Proforma ile aynı kurumsal düzeni kullanır. */
function RaporBelgesi({ veri, aralik, baslik }: {
  veri: RaporVeri; aralik: { baslangic?: string; bitis?: string }; baslik: string;
}) {
  const firma = useVeri<Record<string, unknown>>('/ayarlar');
  const firmaAdi = (firma.veri?.['firma.ad'] as string | undefined) ?? 'NERO PERFORMANCE';
  const iletisim = (firma.veri?.['firma.iletisim'] as string | undefined) ?? '';
  return (
    <div className="belge-sarma">
      <div className="belge">
        <div className="belge-ust">
          <div>
            <div className="belge-logo"><NeroLogo yukseklik={40} baslik="" /></div>
            {firmaAdi.toLocaleUpperCase('tr-TR') !== 'NERO PERFORMANCE' && (
              <div className="belge-firma-alt">{firmaAdi}</div>
            )}
            {iletisim && <div className="belge-iletisim">{iletisim}</div>}
          </div>
          <div>
            <div className="belge-baslik">KASA / GELİR RAPORU</div>
            <div className="belge-no">
              {baslik}<br />
              {tarihGoster(aralik.baslangic)} – {tarihGoster(aralik.bitis)}
            </div>
          </div>
        </div>

        <div className="belge-taraf">
          <div className="belge-kutu">
            <h4>Dönem özeti</h4>
            <div className="satir-veri"><span>Toplam girdi</span><span className="para">{para(veri.ozet.girdi)}</span></div>
            <div className="satir-veri"><span>Toplam çıktı</span><span className="para">{para(veri.ozet.cikti)}</span></div>
            <div className="satir-veri"><span>Net</span><strong className="para">{para(veri.ozet.net)}</strong></div>
            <div className="satir-veri"><span>İşlem sayısı</span><span>{veri.ozet.adet}</span></div>
          </div>
          <div className="belge-kutu">
            <h4>İş emri</h4>
            <div className="satir-veri"><span>Tamamlanan</span><span>{veri.isEmriOzeti.tamamlanan}</span></div>
            <div className="satir-veri"><span>Açık iş</span><span>{veri.isEmriOzeti.acik}</span></div>
            <div className="satir-veri"><span>Ciro</span><span className="para">{para(veri.isEmriOzeti.ciro)}</span></div>
            <div className="satir-veri"><span>Bekleyen tahsilat</span><span className="para">{para(veri.isEmriOzeti.bekleyenTahsilat)}</span></div>
          </div>
        </div>

        {veri.gunluk.length === 0 ? (
          <p style={{ color: '#666' }}>Bu dönemde veri yok.</p>
        ) : (
          <table>
            <thead>
              <tr><th>Tarih</th><th className="sag">Girdi</th><th className="sag">Çıktı</th><th className="sag">Net</th></tr>
            </thead>
            <tbody>
              {veri.gunluk.map((g) => (
                <tr key={g.tarih}>
                  <td>{tarihGoster(g.tarih)}</td>
                  <td className="sag para">{para(g.girdi)}</td>
                  <td className="sag para">{para(g.cikti)}</td>
                  <td className="sag para">{para(g.girdi - g.cikti)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {veri.odemeTipleri.length > 0 && (
          <>
            <h4 style={{ margin: '4mm 0 2mm', fontSize: '9.5pt' }}>Ödeme tipi dağılımı</h4>
            <table>
              <thead><tr><th>Yöntem</th><th className="sag">Adet</th><th className="sag">Tutar</th></tr></thead>
              <tbody>
                {veri.odemeTipleri.map((o) => (
                  <tr key={o.odeme_tipi}>
                    <td>{o.odeme_tipi}</td><td className="sag">{o.adet}</td>
                    <td className="sag para">{para(o.toplam)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {veri.enCokHizmetler.length > 0 && (
          <>
            <h4 style={{ margin: '4mm 0 2mm', fontSize: '9.5pt' }}>En çok yapılan işlemler</h4>
            <table>
              <thead><tr><th>İşlem</th><th className="sag">Adet</th><th className="sag">Tutar</th></tr></thead>
              <tbody>
                {veri.enCokHizmetler.slice(0, 12).map((h) => (
                  <tr key={h.ad}><td>{h.ad}</td><td className="sag">{h.adet}</td><td className="sag para">{para(h.toplam)}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {veri.enCokUrunler.length > 0 && (
          <>
            <h4 style={{ margin: '4mm 0 2mm', fontSize: '9.5pt' }}>En çok satılan ürünler</h4>
            <table>
              <thead><tr><th>Ürün</th><th className="sag">Adet</th><th className="sag">Tutar</th></tr></thead>
              <tbody>
                {veri.enCokUrunler.slice(0, 12).map((u) => (
                  <tr key={u.ad}><td>{u.ad}</td><td className="sag">{u.adet}</td><td className="sag para">{para(u.toplam)}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="belge-alt">
          <span>{firmaAdi} · Nero Pro</span>
          <span>Rapor tarihi: {tarihGoster(bugun())}</span>
        </div>
      </div>
    </div>
  );
}


/* ---------- Firma ve belge bilgileri ---------- */

interface BelgeAlani {
  anahtar: string; etiket: string; ipucu: string; varsayilan: string; cokSatir?: boolean;
}

const BELGE_ALANLARI: BelgeAlani[] = [
  { anahtar: 'firma.ad', etiket: 'Firma adı', ipucu: 'Proforma ve raporların başlığında görünür', varsayilan: 'NERO PERFORMANCE' },
  { anahtar: 'firma.iletisim', etiket: 'İletişim bilgisi', ipucu: 'Adres · telefon · e-posta', varsayilan: '', cokSatir: true },
  { anahtar: 'proforma.belge_basligi', etiket: 'Proforma belge başlığı', ipucu: '', varsayilan: 'PROFORMA FATURA' },
  { anahtar: 'proforma.yetkili', etiket: 'Yetkili', ipucu: 'Belgede imza satırında görünür', varsayilan: '' },
  { anahtar: 'proforma.banka_adi', etiket: 'Banka adı', ipucu: '', varsayilan: '' },
  { anahtar: 'proforma.iban', etiket: 'IBAN', ipucu: '', varsayilan: '' },
  { anahtar: 'proforma.alt_aciklama', etiket: 'Alt açıklama', ipucu: 'Her yeni proformaya hazır gelir', varsayilan: '', cokSatir: true },
];

function Firma() {
  const { veri, yukleniyor, yenile } = useVeri<Record<string, unknown>>('/ayarlar');
  const [taslak, setTaslak] = useState<Record<string, string>>({});
  const bildir = useBildirim();

  useEffect(() => {
    if (!veri) return;
    const t: Record<string, string> = {};
    for (const a of BELGE_ALANLARI) t[a.anahtar] = String(veri[a.anahtar] ?? a.varsayilan);
    setTaslak(t);
  }, [veri]);

  const kaydet = useEylem(async () => {
    for (const a of BELGE_ALANLARI) {
      await api.guncelle(`/ayarlar/${a.anahtar}`, { deger: taslak[a.anahtar] ?? '' });
    }
    yenile();
    bildir('Firma bilgileri kaydedildi');
  });

  if (yukleniyor) return <Yukleniyor satir={4} />;

  return (
    <div className="sutun" style={{ gap: 'var(--b4)', maxWidth: 720 }}>
      <Kart>
        <KartBaslik baslik="Belge bilgileri" />
        <div className="kart-govde sutun">
          <UyariSerit ton="notr">
            Buradaki bilgiler yeni proformalara ve rapor çıktılarına hazır gelir.
            Kayıtlı bir belgede sonradan değişiklik yapmaz.
          </UyariSerit>
          {BELGE_ALANLARI.map((a) => (
            a.cokSatir ? (
              <MetinAlani
                key={a.anahtar} etiket={a.etiket} ipucu={a.ipucu || undefined} rows={3}
                value={taslak[a.anahtar] ?? ''}
                onChange={(v) => setTaslak((t) => ({ ...t, [a.anahtar]: v }))}
              />
            ) : (
              <Girdi
                key={a.anahtar} etiket={a.etiket} ipucu={a.ipucu || undefined}
                value={taslak[a.anahtar] ?? ''}
                onChange={(v) => setTaslak((t) => ({ ...t, [a.anahtar]: v }))}
              />
            )
          ))}
          {kaydet.hata && <UyariSerit ton="kritik">{kaydet.hata}</UyariSerit>}
          <div>
            <button className="btn btn-birincil" onClick={() => void kaydet.yurut(undefined as never)}
              disabled={kaydet.calisiyor}>
              {kaydet.calisiyor ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      </Kart>

      <KurKarti />
    </div>
  );
}

/* ---------- Döviz kuru ---------- */

/**
 * KUR PANELİ — normalde HİÇ AÇILMAYAN bir ekran.
 *
 * Kullanıcının isteği açıktı: "güncel kur yazmaya gerek yok." Kur her gün
 * TCMB'den kendiliğinden gelir ve hiçbir yerde sorulmaz. Bu panel iki iş
 * için var:
 *   1. Hangi kurun, hangi tarihle, hangi kaynaktan kullanıldığını GÖRMEK.
 *   2. Otomatik çekim bir sebeple çalışmıyorsa (kapalı ağ, engelli çıkış)
 *      kuru elle girip sistemi çalışır tutmak.
 * İkincisi bir son çaredir; ekranda da öyle anlatılır.
 */
function KurKarti() {
  const bildir = useBildirim();
  const { veri, yukleniyor, yenile } = useVeri<KurDurumu>('/kur');
  const [elle, setElle] = useState<Record<string, string>>({});

  const tazele = useEylem(async () => {
    const s = await api.gonder<{ yenilendi: boolean; not: string; tazelenenUrun: number }>('/kur/yenile', {});
    kuruTazele();
    yenile();
    bildir(s.yenilendi
      ? `Kur güncellendi${s.tazelenenUrun ? ` · ${s.tazelenenUrun} dövizli ürün fiyatı tazelendi` : ''}`
      : s.not || 'Kur güncellenemedi');
  });

  const elleKaydet = useEylem(async () => {
    const govde: Record<string, number> = {};
    for (const kod of DOVIZ_KODLARI) {
      const v = Number(elle[kod]);
      if (Number.isFinite(v) && v > 0) govde[kod] = v;
    }
    await api.guncelle('/kur', govde);
    kuruTazele();
    yenile();
    setElle({});
    bildir('Kur elle kaydedildi');
  });

  return (
    <Kart>
      <KartBaslik baslik="Döviz kuru" />
      <div className="kart-govde sutun">
        {yukleniyor ? <Yukleniyor satir={2} /> : (
          <>
            <UyariSerit ton={veri?.bayat ? 'uyari' : 'notr'}>
              {veri?.bayat
                ? 'Kur güncellenemiyor; aşağıdaki değerler en son bilinen kurdur. '
                  + 'Döviz girişi çalışmaya devam eder ama TL karşılıkları eski kurla hesaplanır.'
                : 'Kur her gün TCMB’den kendiliğinden alınır. Kasa ve stok ekranlarında '
                  + 'kur sormaz — yalnız bilgi olarak gösterir.'}
            </UyariSerit>
            <div className="satir" style={{ gap: 'var(--b4)', flexWrap: 'wrap' }}>
              {DOVIZ_KODLARI.map((kod) => (
                <Olcum
                  key={kod}
                  etiket={`1 ${paraBirimiSimgesi(kod)} (${kod})`}
                  deger={veri?.kurlar?.[kod]
                    ? `${veri.kurlar[kod]!.toLocaleString('tr-TR', { maximumFractionDigits: 4 })} ₺`
                    : '—'}
                  alt={veri ? `${tarihGoster(veri.tarih)} · ${KUR_KAYNAK_ADI[veri.kaynak] ?? veri.kaynak}` : ''}
                />
              ))}
            </div>
            <div className="satir">
              <button className="btn btn-sade" onClick={() => void tazele.yurut(undefined as never)}
                disabled={tazele.calisiyor}>
                {tazele.calisiyor ? 'Alınıyor…' : 'TCMB’den şimdi güncelle'}
              </button>
            </div>
            {tazele.hata && <UyariSerit ton="kritik">{tazele.hata}</UyariSerit>}

            <details>
              <summary style={{ cursor: 'pointer', color: 'var(--metin-2)', fontSize: 'var(--p-13)' }}>
                Kuru elle gir (yalnız otomatik çekim çalışmıyorsa)
              </summary>
              <div className="sutun" style={{ marginTop: 'var(--b3)' }}>
                <div className="izgara izgara-2">
                  {DOVIZ_KODLARI.map((kod) => (
                    <ParaGirdi
                      key={kod} etiket={`1 ${kod} kaç ₺`} deger={elle[kod] ?? ''}
                      onChange={(v) => setElle((o) => ({ ...o, [kod]: v }))}
                      step="0.0001"
                    />
                  ))}
                </div>
                <div>
                  <button className="btn btn-sade" onClick={() => void elleKaydet.yurut(undefined as never)}
                    disabled={elleKaydet.calisiyor || !DOVIZ_KODLARI.some((k) => Number(elle[k]) > 0)}>
                    {elleKaydet.calisiyor ? 'Kaydediliyor…' : 'Elle girilen kuru kaydet'}
                  </button>
                </div>
                <UyariSerit ton="notr">
                  Elle girilen kur, bir sonraki başarılı TCMB çekiminde değişir.
                  Geçmiş kayıtların kuru DEĞİŞMEZ: her işlem kendi kurunu taşır.
                </UyariSerit>
                {elleKaydet.hata && <UyariSerit ton="kritik">{elleKaydet.hata}</UyariSerit>}
              </div>
            </details>
          </>
        )}
      </div>
    </Kart>
  );
}

const KUR_KAYNAK_ADI: Record<string, string> = {
  TCMB: 'TCMB',
  elle: 'elle girildi',
  tohum: 'varsayılan kur',
};

/* ---------- Kullanıcılar ---------- */

function Kullanicilar() {
  const { veri, yukleniyor, yenile } = useVeri<Kullanici[]>('/kullanicilar');
  const [duzenlenen, setDuzenlenen] = useState<Kullanici | 'yeni' | null>(null);

  return (
    <div className="sutun" style={{ gap: 'var(--b4)' }}>
      <div className="satir" style={{ justifyContent: 'flex-end' }}>
        <button className="btn btn-birincil" onClick={() => setDuzenlenen('yeni')}>
          <Simge ad="arti" className="simge" /> Kullanıcı ekle
        </button>
      </div>
      <Kart>
        {yukleniyor ? <div className="kart-govde"><Yukleniyor /></div> : (
          <div className="tablo-sarma">
            <table className="tablo">
              <thead><tr><th>Ad soyad</th><th>Kullanıcı adı</th><th>Rol</th><th>Son giriş</th><th>Durum</th><th /></tr></thead>
              <tbody>
                {(veri ?? []).map((k) => (
                  <tr key={k.id}>
                    <td className="ana">{k.ad_soyad}</td>
                    <td className="ikincil sayi">{k.kullanici_adi}</td>
                    <td><Rozet ton={k.rol === 'yonetici' ? 'bilgi' : 'notr'}>{ROL_ADI[k.rol]}</Rozet></td>
                    <td className="ikincil">{k.son_giris_at ? tarihSaatGoster(k.son_giris_at) : 'Hiç girmedi'}</td>
                    <td><Rozet ton={k.aktif ? 'olumlu' : 'notr'}>{k.aktif ? 'Aktif' : 'Pasif'}</Rozet></td>
                    <td className="dar">
                      <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setDuzenlenen(k)} aria-label="Düzenle">
                        <Simge ad="kalem" boyut={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Kart>
      <UyariSerit ton="notr" baslik="Yetkiler roller üzerinden yönetilir">
        <strong>Personel</strong> günlük işin tamamını yapabilir: iş emri, müşteri ve araç, kasa
        hareketi, cari, stok hareketi, proforma, fiyat kataloğunu görüntüleme, Log Analiz ve
        Post Studio.<br />
        <strong>Yöneticiye özel:</strong> kayıt silme, fiyat düzenleme, raporlar, kullanıcı
        yönetimi, sistem ayarları, yedekleme, denetim kaydı ve çöp kutusu.<br />
        Bu kısıtlar sunucuda uygulanır; menüyü gizlemek tek başına yetkilendirme değildir.
      </UyariSerit>

      {duzenlenen && (
        <KullaniciPenceresi
          mevcut={duzenlenen === 'yeni' ? undefined : duzenlenen}
          kapat={() => setDuzenlenen(null)}
          kaydedildi={() => { setDuzenlenen(null); yenile(); }}
        />
      )}
    </div>
  );
}

function KullaniciPenceresi({ mevcut, kapat, kaydedildi }: {
  mevcut?: Kullanici; kapat: () => void; kaydedildi: () => void;
}) {
  const bildir = useBildirim();
  const [adSoyad, setAdSoyad] = useState(mevcut?.ad_soyad ?? '');
  const [kullaniciAdi, setKullaniciAdi] = useState(mevcut?.kullanici_adi ?? '');
  const [rol, setRol] = useState<string>(mevcut?.rol ?? 'personel');
  const [aktif, setAktif] = useState(mevcut ? mevcut.aktif === 1 : true);
  const [sifre, setSifre] = useState('');

  const { yurut, calisiyor, hata } = useEylem(async () => {
    const govde = { ad_soyad: adSoyad, kullanici_adi: kullaniciAdi, rol, aktif: aktif ? 1 : 0, sifre: sifre || undefined };
    if (mevcut) await api.guncelle(`/kullanicilar/${mevcut.id}`, govde);
    else await api.gonder('/kullanicilar', govde);
    bildir(mevcut ? 'Kullanıcı güncellendi' : 'Kullanıcı eklendi');
    kaydedildi();
  });

  return (
    <Pencere
      baslik={mevcut ? 'Kullanıcıyı düzenle' : 'Yeni kullanıcı'}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)}
            disabled={calisiyor || !adSoyad.trim() || !kullaniciAdi.trim() || (!mevcut && sifre.length < 8)}>
            Kaydet
          </button>
        </>
      }
    >
      <div className="sutun">
        <Girdi etiket="Ad soyad" value={adSoyad} onChange={setAdSoyad} autoFocus required duzen="ozelAd" />
        <Girdi etiket="Kullanıcı adı" value={kullaniciAdi} onChange={setKullaniciAdi} required
          ipucu="Yalnızca harf, rakam, nokta ve tire" />
        <Secim etiket="Rol" value={rol} onChange={setRol}
          secenekler={ROLLER.map((r) => ({ deger: r, ad: ROL_ADI[r] }))} />
        <Girdi
          etiket={mevcut ? 'Yeni şifre' : 'Şifre'} type="password" value={sifre} onChange={setSifre}
          autoComplete="new-password"
          ipucu={mevcut ? 'Boş bırakılırsa şifre değişmez. Değiştirilirse kullanıcının oturumları kapanır.' : 'En az 8 karakter'}
        />
        <Onay etiket="Aktif (giriş yapabilir)" isaretli={aktif} onChange={setAktif} />
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}

/* ---------- Google Drive bulut yedeği ---------- */

/** Koşu durumları renkli ama SÜSSÜZ: "tamam değil" hemen görünsün. */
function DurumRozeti({ durum }: { durum: string }) {
  const ton = durum === 'tamam' ? 'olumlu'
    : durum === 'hata' ? 'kritik'
      : durum === 'calisiyor' ? 'bilgi' : 'notr';
  const ad = { tamam: 'Tamam', hata: 'Hata', calisiyor: 'Çalışıyor', atlandi: 'Atlandı', bekliyor: 'Bekliyor' }[durum]
    ?? durum;
  return <Rozet ton={ton as 'olumlu' | 'kritik' | 'bilgi' | 'notr'}>{ad}</Rozet>;
}

/**
 * BULUT YEDEĞİ EKRANI — yalnızca yönetici.
 *
 * Bu ekran üç soruyu net cevaplar:
 *   1. Drive bağlı mı, hangi hesaba?
 *   2. Son yedek ne zaman alındı, GERÇEKTEN Drive'a gitti mi?
 *   3. Yedek geri yüklenebilir durumda mı?
 *
 * "Sunucu yedeği" ile "Drive yedeği" AYRI gösterilir. Biri tamam diğeri
 * hata olabilir; tek bir yeşil tikte birleştirmek, olmayan bir güvenlik
 * duygusu verirdi.
 */
function BulutYedegi({ veri, yenile }: { veri: YedekDurumRaporu; yenile: () => void }) {
  const bildir = useBildirim();
  const [kesOnayi, setKesOnayi] = useState(false);

  const baglan = useEylem(async () => {
    const { url } = await api.al<{ url: string }>('/google-drive/oauth/start');
    /* Yeni sekmede açılır: yönetici bu ekrandan çıkmaz, Google'dan
       dönünce buraya "Yenile" ile geri gelir. */
    window.open(url, '_blank', 'noopener');
  });

  const sina = useEylem(async () => {
    const s = await api.gonder<{ tamam: boolean; mesaj: string }>('/google-drive/sina');
    yenile();
    bildir(s.mesaj);
  });

  const baglantiyiKes = useEylem(async () => {
    const s = await api.gonder<{ mesaj: string }>('/google-drive/baglantiyi-kes');
    setKesOnayi(false);
    yenile();
    bildir(s.mesaj);
  });

  const simdiYedekle = useEylem(async () => {
    const s = await api.gonder<{
      sunucuDurum: string; driveDurum: string; hata: string;
      serverDosyalari?: { yeniYuklenen: number; zatenYedekli: number };
    }>('/yedek/simdi');
    yenile();
    const d = s.serverDosyalari;
    const dosyaNotu = d ? ` · Dosya: ${d.yeniYuklenen} yeni, ${d.zatenYedekli} zaten yedekli` : '';
    bildir(
      s.sunucuDurum === 'tamam' && s.driveDurum !== 'hata'
        ? `Yedek alındı · Sunucu: tamam · Drive: ${s.driveDurum === 'tamam' ? 'tamam' : 'atlandı'}${dosyaNotu}`
        : `Yedek TAMAMLANMADI · Sunucu: ${s.sunucuDurum} · Drive: ${s.driveDurum}${s.hata ? ` · ${s.hata}` : ''}`,
    );
  });

  const dogrula = useEylem(async () => {
    const s = await api.gonder<{ gecerli: boolean; bulgular: string[] }>('/yedek/dogrula');
    yenile();
    bildir(s.gecerli ? 'Yedek doğrulandı: geri yüklenebilir görünüyor'
      : `Doğrulama uyarısı: ${s.bulgular[0] ?? 'bilinmeyen sorun'}`);
  });

  const d = veri.drive;

  /* Yapılandırma eksikse önce ONU söyle: "bağlan" düğmesine basıp
     anlaşılmaz bir hata almaktansa neyin eksik olduğu yazsın. */
  const eksikler: string[] = [];
  if (!d.yapilandirildi) eksikler.push('GOOGLE_DRIVE_CLIENT_ID / GOOGLE_DRIVE_CLIENT_SECRET');
  if (!d.sifrelemeHazir) eksikler.push('GOOGLE_DRIVE_TOKEN_ENCRYPTION_KEY');

  const sonDrive = veri.sonBasariliDrive;

  return (
    <div className="sutun" style={{ gap: 'var(--b4)' }}>
      {eksikler.length > 0 && (
        <UyariSerit ton="kritik" baslik="Google Drive yedeklemesi yapılandırılmamış">
          Sunucuda tanımlanması gereken ortam değişkenleri: {eksikler.join(', ')}.
          Bunlar tanımlanmadan bağlantı kurulamaz — sistem şifresiz saklamaya DÜŞMEZ.
        </UyariSerit>
      )}

      {eksikler.length === 0 && !d.bagli && (
        <UyariSerit ton="uyari" baslik="Google Drive bağlı değil">
          <strong>Veritabanı yedeği</strong> yine de bu sunucuda alınıyor — ama uzun
          süreli arşiv yok: sunucu kaybolursa geri dönüş yoktur.
          {' '}<strong>Post Studio görselleri ve ham log dosyaları ise
          arşivlenemez</strong>; bunlar sunucuya kaydedilmez, Drive bağlantısı ister.
        </UyariSerit>
      )}

      {d.bagli && sonDrive && (
        <UyariSerit ton="olumlu" baslik="Drive arşivi çalışıyor">
          Son başarılı Drive yedeği: {sonDrive.gun}
          {sonDrive.bitis && <> · {tarihSaatGoster(sonDrive.bitis)}</>}
        </UyariSerit>
      )}
      {d.bagli && !sonDrive && (
        <UyariSerit ton="uyari" baslik="Henüz Drive'a yedek gitmedi">
          Bağlantı kurulu ama başarıyla tamamlanmış bir Drive yedeği yok.
          “Şimdi yedekle” ile hemen deneyebilirsiniz.
        </UyariSerit>
      )}

      <div className="izgara izgara-2">
        <Kart>
          <KartBaslik baslik="Google Drive bağlantısı" />
          <div className="kart-govde">
            <dl className="tanim">
              <dt>Durum</dt>
              <dd>{d.bagli ? <Rozet ton="olumlu">Bağlı</Rozet> : <Rozet ton="uyari">Bağlı değil</Rozet>}</dd>
              <dt>Hesap</dt>
              <dd style={{ wordBreak: 'break-all' }}>{d.hesap || '—'}</dd>
              <dt>Bağlanma</dt>
              <dd>{d.baglandiAt ? tarihSaatGoster(d.baglandiAt) : '—'}</dd>
              <dt>İzin kapsamı</dt>
              <dd style={{ fontSize: 'var(--p-12)' }}>
                drive.file — yalnızca bu uygulamanın oluşturduğu dosyalar
              </dd>
              <dt>Klasör</dt>
              <dd style={{ fontSize: 'var(--p-12)' }}>NERO PRO - YEDEKLER</dd>
            </dl>
            <div className="satir" style={{ marginTop: 'var(--b5)', flexWrap: 'wrap', gap: 'var(--b3)' }}>
              {!d.bagli ? (
                <button
                  className="btn btn-birincil" disabled={baglan.calisiyor || eksikler.length > 0}
                  onClick={() => void baglan.yurut(undefined as never)}
                >
                  Google Drive'a bağlan
                </button>
              ) : (
                <>
                  <button className="btn btn-ikincil btn-kucuk" disabled={sina.calisiyor}
                    onClick={() => void sina.yurut(undefined as never)}>
                    {sina.calisiyor ? 'Sınanıyor…' : 'Bağlantıyı sına'}
                  </button>
                  <button className="btn btn-sade btn-kucuk" onClick={() => setKesOnayi(true)}>
                    Bağlantıyı kes
                  </button>
                </>
              )}
            </div>
            {baglan.hata && <UyariSerit ton="kritik">{baglan.hata}</UyariSerit>}
            {sina.hata && <UyariSerit ton="kritik">{sina.hata}</UyariSerit>}
            <p style={{ marginTop: 'var(--b4)', fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
              Canlı veritabanı Drive üzerinde ÇALIŞMAZ. Drive yalnızca uzun süreli yedek
              arşivi ve büyük görsel/log dosyaları içindir. Bağlantı kesildiğinde
              Drive'daki dosyalar silinmez.
            </p>
          </div>
        </Kart>

        <Kart>
          <KartBaslik baslik="Yedek durumu" />
          <div className="kart-govde">
            {/* SUNUCU ve DRIVE AYRI OKUNUR. İkisini tek bir "başarılı"
                rozetinde birleştirmek, biri çalışıp diğeri çalışmazken
                olmayan bir güvenlik duygusu verirdi. */}
            <dl className="tanim">
              <dt style={{ gridColumn: '1 / -1', fontWeight: 'var(--kalin)' }}>Sunucu yedeği</dt>
              <dt>Son yedek</dt>
              <dd>
                {veri.sonKosu
                  ? <>{veri.sonKosu.gun} <DurumRozeti durum={veri.sonKosu.sunucu_durum} /></>
                  : '—'}
              </dd>
              <dt>Elde duran</dt>
              <dd>
                {veri.sunucuYedekGunleri.length} gün · {boyutGoster(veri.sunucuToplamBayt)}
              </dd>
              <dt>Saklama</dt>
              <dd>{veri.saklamaGunu} gün (sonra otomatik silinir)</dd>
              <dt>Doğrulama</dt>
              <dd>
                {veri.dogrulama
                  ? (veri.dogrulama.gecerli
                    ? <Rozet ton="olumlu">Geri yüklenebilir</Rozet>
                    : <Rozet ton="kritik">{veri.dogrulama.bulgular.length} uyarı</Rozet>)
                  : '—'}
              </dd>

              <dt style={{ gridColumn: '1 / -1', fontWeight: 'var(--kalin)', paddingTop: 'var(--b3)' }}>
                Drive yedeği
              </dt>
              <dt>Son başarılı</dt>
              <dd>
                {sonDrive
                  ? <>{sonDrive.gun} <DurumRozeti durum={sonDrive.drive_durum} /></>
                  : <Rozet ton="uyari">Yok</Rozet>}
              </dd>
              <dt>Son koşu</dt>
              <dd>
                {veri.sonKosu ? <DurumRozeti durum={veri.sonKosu.drive_durum} /> : '—'}
              </dd>
              <dt>Saklama</dt>
              <dd>Süresiz — otomatik silinmez</dd>
              <dt>Sunucu dosyaları</dt>
              <dd>
                {veri.yedeksizDosyalar.length === 0
                  ? <Rozet ton="olumlu">Hepsi yedekli</Rozet>
                  : <Rozet ton="kritik">{veri.yedeksizDosyalar.length} dosya yedeksiz</Rozet>}
              </dd>
            </dl>
            <div className="satir" style={{ marginTop: 'var(--b5)', flexWrap: 'wrap', gap: 'var(--b3)' }}>
              <button className="btn btn-birincil" disabled={simdiYedekle.calisiyor}
                onClick={() => void simdiYedekle.yurut(undefined as never)}>
                <Simge ad="yedek" className="simge" />
                {simdiYedekle.calisiyor ? 'Yedekleniyor…' : 'Şimdi yedekle'}
              </button>
              <button className="btn btn-ikincil btn-kucuk" disabled={dogrula.calisiyor}
                onClick={() => void dogrula.yurut(undefined as never)}>
                {dogrula.calisiyor ? 'Doğrulanıyor…' : 'Yedeği doğrula'}
              </button>
            </div>
            {simdiYedekle.hata && <UyariSerit ton="kritik">{simdiYedekle.hata}</UyariSerit>}
            {dogrula.hata && <UyariSerit ton="kritik">{dogrula.hata}</UyariSerit>}
            {veri.dogrulama && !veri.dogrulama.gecerli && (
              <UyariSerit ton="kritik" baslik="Son yedekte sorun bulundu">
                <ul style={{ margin: 0, paddingLeft: '1.1em' }}>
                  {veri.dogrulama.bulgular.map((b: string) => <li key={b}>{b}</li>)}
                </ul>
              </UyariSerit>
            )}
            {/* FELAKET KURTARMA EKSİKSE SÖYLE.
                Veritabanı yedeği kusursuz olabilir ama sunucuda duran
                ECU/TCU dosyalarının Drive'da karşılığı yoksa arşiv
                eksiktir; bunu bilmemek, bilmekten tehlikelidir. */}
            {veri.yedeksizDosyalar.length > 0 && (
              <UyariSerit ton="kritik" baslik="Bazı sunucu dosyalarının Drive yedeği yok">
                {veri.yedeksizDosyalar.length} dosya yalnızca bu sunucuda duruyor:
                {' '}{veri.yedeksizDosyalar.slice(0, 5).map((d) => d.ad).join(', ')}
                {veri.yedeksizDosyalar.length > 5 && ` … (+${veri.yedeksizDosyalar.length - 5})`}
                . “Şimdi yedekle” bunları Google Drive'daki “12 - Dosyalar”
                klasörüne yükler.
              </UyariSerit>
            )}
          </div>
        </Kart>
      </div>

      <Kart>
        <KartBaslik baslik="Yedek koşuları" />
        {veri.gecmis.length === 0 ? (
          <BosDurum simge="yedek" baslik="Henüz koşu yok"
            aciklama="Günlük yedek çalıştığında ya da “Şimdi yedekle” dendiğinde burada listelenir." />
        ) : (
          <div className="tablo-sarma">
            <table className="tablo">
              <thead>
                <tr>
                  <th>Gün</th><th>Tip</th><th>Sunucu</th><th>Drive</th>
                  <th className="sag">Boyut</th><th>Tetikleyen</th><th>Not</th>
                </tr>
              </thead>
              <tbody>
                {veri.gecmis.map((k: YedekKosusu) => (
                  <tr key={k.id}>
                    <td className="dar">{k.gun}</td>
                    <td className="ikincil dar">{k.tip === 'gunluk' ? 'Günlük' : 'Elle'}</td>
                    <td className="dar"><DurumRozeti durum={k.sunucu_durum} /></td>
                    <td className="dar"><DurumRozeti durum={k.drive_durum} /></td>
                    <td className="sag ikincil">{k.boyut ? boyutGoster(k.boyut) : '—'}</td>
                    <td className="ikincil dar">{k.tetikleyen || '—'}</td>
                    <td className="ikincil" style={{ fontSize: 'var(--p-12)' }}>{k.hata || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Kart>

      {kesOnayi && (
        <OnayPenceresi
          baslik="Google Drive bağlantısı kesilsin mi?"
          aciklama={
            <>
              Uygulamanın Drive erişimi kaldırılır ve saklanan yetkilendirme anahtarı silinir.
              {' '}<strong>Drive'daki yedek dosyalarınız SİLİNMEZ</strong>, hesabınızda kalır.
              {' '}Bağlantı kesikken günlük yedek yalnızca sunucuya alınır.
            </>
          }
          onayMetni="Bağlantıyı kes"
          tehlike={false}
          calisiyor={baglantiyiKes.calisiyor}
          hata={baglantiyiKes.hata}
          onayla={() => void baglantiyiKes.yurut(undefined as never)}
          kapat={() => setKesOnayi(false)}
        />
      )}
    </div>
  );
}

/* ---------- Yedekleme ---------- */

/**
 * YEDEKLEME SEKMESİ — TEK YEDEK SİSTEMİ GÖRÜNÜR.
 *
 * ─── DÜZELTİLEN KARIŞIKLIK ───
 * Bu ekranda bir süre İKİ yedek sistemi yan yana duruyordu: yeni bulut
 * yedeği ve eski yerel zamanlayıcının paneli. İki "Şimdi yedekle"
 * düğmesi, iki "son yedek" tarihi ve iki ayrı klasör ayarı vardı.
 * Yöneticinin hangi düğmenin neyi yedeklediğini bilememesi, yedeksiz
 * kalmakla eş değerdir.
 *
 * ─── ŞİMDİ ───
 * Sunucuda (production / PostgreSQL) TEK panel görünür: bulut yedeği.
 * Eski yerel zamanlayıcının kartları YALNIZCA o zamanlayıcının gerçekten
 * çalıştığı kurulumda (dükkândaki Mac · yerel SQLite) çizilir — orada
 * hâlâ doğru bilgiyi verirler.
 *
 * Geri yükleme, eski veri içe aktarma ve yazım düzeltme gerçek yönetici
 * araçlarıdır; silinmediler, ayrı bir başlık altına alındılar.
 */
function Yedekleme() {
  const bildir = useBildirim();
  const rapor = useVeri<YedekDurumRaporu>('/yedek/durum-detay');
  const { veri, yukleniyor, yenile } = useVeri<YedekDurumu>('/yedek');
  const [yedekKlasor, setYedekKlasor] = useState('');
  const [driveKlasor, setDriveKlasor] = useState('');
  const [geriYuklenecek, setGeriYuklenecek] = useState<YedekDosya | null>(null);
  const eskiDosyaRef = useRef<HTMLInputElement>(null);

  const klasorKaydet = useEylem(async ({ alan, yol }: { alan: 'yedek' | 'drive'; yol: string }) => {
    await api.gonder(alan === 'yedek' ? '/yedek/klasor' : '/yedek/drive-klasor', { yol });
    yenile();
    bildir(alan === 'yedek' ? 'Yedek klasörü güncellendi' : 'Google Drive klasörü güncellendi');
    if (alan === 'yedek') setYedekKlasor(''); else setDriveKlasor('');
  });

  const geriYukle = useEylem(async (dosya: string) => {
    const s = await api.gonder<{ sonuc: { guvenlikYedegi: string; geriYuklenen: string } }>(
      '/yedek/geri-yukle', { dosya },
    );
    setGeriYuklenecek(null);
    yenile();
    bildir(`${s.sonuc.geriYuklenen} geri yüklendi · güvenlik yedeği: ${s.sonuc.guvenlikYedegi}`);
  });

  const eskiVeri = useEylem(async (dosya: File) => {
    const metin = await dosya.text();
    const s = await api.gonder<{
      musteriler: number; cariHareketler: number; urunler: number; kasaHareketleri: number; atlanan: string[];
    }>('/yedek/eski-veri', { veri: JSON.parse(metin) });
    bildir(
      `İçe aktarıldı → ${s.musteriler} müşteri, ${s.cariHareketler} cari işlem, `
      + `${s.urunler} ürün, ${s.kasaHareketleri} kasa kaydı`,
    );
  });

  /* ---- Eski kayıtlarda yazım düzeltmesi ---- */
  const [duzeltmeOnizleme, setDuzeltmeOnizleme] = useState<YazimDuzeltmeSonucu | null>(null);
  const [duzeltmeSonucu, setDuzeltmeSonucu] = useState<YazimDuzeltmeSonucu | null>(null);

  const yazimOnizle = useEylem(async () => {
    setDuzeltmeSonucu(null);
    setDuzeltmeOnizleme(await api.gonder<YazimDuzeltmeSonucu>('/yedek/yazim-duzelt', { uygula: false }));
  });

  const yazimUygula = useEylem(async () => {
    const s = await api.gonder<YazimDuzeltmeSonucu>('/yedek/yazim-duzelt', { uygula: true });
    setDuzeltmeOnizleme(null);
    setDuzeltmeSonucu(s);
    yenile();
    bildir(`${s.degisen} alan düzeltildi · güvenlik yedeği alındı`);
  });

  if (yukleniyor || !veri || rapor.yukleniyor || !rapor.veri) return <Yukleniyor satir={4} />;

  const sonGun = veri.sonBasariliAt ? veri.sonBasariliAt.slice(0, 10) : '';
  const guncel = sonGun === bugun();
  /* Eski yerel zamanlayıcı bu kurulumda çalışıyor mu? Sunucuda HAYIR. */
  const legacy = rapor.veri.legacyZamanlayici;

  return (
    <div className="sutun" style={{ gap: 'var(--b4)' }}>
      {/* PRODUCTION'DA GÖRÜNEN TEK YEDEK PANELİ. */}
      <BulutYedegi veri={rapor.veri} yenile={() => { rapor.yenile(); yenile(); }} />

      {legacy && (
        <>
          <h3 style={{ margin: 'var(--b5) 0 0', fontSize: 'var(--p-15)' }}>
            Bu bilgisayardaki yerel yedekler
          </h3>
          <UyariSerit ton="notr">
            Aşağıdaki bölüm yalnızca bu makinede kurulu YEREL sürüm içindir.
            Sunucuda günlük yedeği yukarıdaki bulut sistemi alır.
          </UyariSerit>
          {veri.sonHata ? (
            <UyariSerit ton="kritik" baslik="Son yerel yedekleme başarısız oldu">
              {veri.sonHata}
              {veri.sonHataAt && <> · {tarihSaatGoster(veri.sonHataAt)}</>}
            </UyariSerit>
          ) : guncel ? (
            <UyariSerit ton="olumlu" baslik="Yerel yedekleme güncel">
              Son başarılı yerel yedek: {tarihSaatGoster(veri.sonBasariliAt)}
            </UyariSerit>
          ) : (
            <UyariSerit ton="uyari" baslik="Bugün henüz yerel yedek alınmadı">
              {veri.sonBasariliAt ? `Son yerel yedek: ${tarihSaatGoster(veri.sonBasariliAt)}` : 'Hiç yerel yedek alınmamış.'}
              {' '}Bilgisayar açıldığında ve her gün otomatik olarak alınır.
            </UyariSerit>
          )}
          <div className="izgara izgara-2">
        <Kart>
          <KartBaslik baslik="Durum" />
          <div className="kart-govde">
            <dl className="tanim">
              <dt>Son yedek</dt>
              <dd>{veri.sonBasariliAt ? tarihSaatGoster(veri.sonBasariliAt) : '—'}</dd>
              <dt>Yedek klasörü</dt>
              <dd style={{ wordBreak: 'break-all', fontSize: 'var(--p-12)' }}>
                {veri.klasor}
                {veri.klasorSecildi && <div><Rozet ton="notr" sade>elle seçildi</Rozet></div>}
              </dd>
              <dt>Google Drive</dt>
              <dd style={{ wordBreak: 'break-all', fontSize: 'var(--p-12)' }}>
                {veri.driveKlasor ? <>{veri.driveKlasor}<div><Rozet ton="olumlu">Bağlı</Rozet></div></>
                  : <Rozet ton="uyari">Bulunamadı</Rozet>}
              </dd>
              <dt>Saklanan</dt>
              <dd>{veri.yedekler.length} yedek</dd>
            </dl>
            {/* İKİNCİ "Şimdi yedekle" DÜĞMESİ YOK.
                Elle yedek almanın tek yeri yukarıdaki paneldir; iki düğme
                iki farklı yedek üretir ve yönetici hangisinin geri
                yükleneceğini bilemez. */}
            <p style={{ marginTop: 'var(--b4)', fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
              Bu kutu bu BİLGİSAYARDAKİ yerel kopyayı gösterir. Elle yedek almak ve
              Google Drive arşivini yönetmek için yukarıdaki paneli kullanın.
              Ana veritabanı hiçbir durumda Drive üzerinde çalışmaz.
            </p>
          </div>
        </Kart>

        <Kart>
          <KartBaslik baslik="Klasörler" />
          <div className="kart-govde sutun">
            <Girdi
              etiket="Yedek klasörü" value={yedekKlasor} onChange={setYedekKlasor}
              placeholder={veri.klasor}
              ipucu="Harici disk veya ağ klasörünün tam yolu. Boş bırakıp kaydederseniz varsayılana döner."
            />
            <div className="satir">
              <button className="btn btn-ikincil btn-kucuk" disabled={klasorKaydet.calisiyor}
                onClick={() => void klasorKaydet.yurut({ alan: 'yedek', yol: yedekKlasor })}>
                Yedek klasörünü kaydet
              </button>
            </div>
            <Girdi
              etiket="Google Drive Masaüstü klasörü" value={driveKlasor} onChange={setDriveKlasor}
              placeholder={veri.driveKlasor ?? 'Otomatik aranır'}
              ipucu={
                'YALNIZCA dükkândaki bilgisayara kurulu sürüm içindir: Google Drive Masaüstü '
                + 'uygulamasının klasörü. Sunucuda bu yol kullanılmaz — orada Drive yedeği '
                + 'yukarıdaki bulut bağlantısı üzerinden gider.'
              }
            />
            <div className="satir">
              <button className="btn btn-ikincil btn-kucuk" disabled={klasorKaydet.calisiyor}
                onClick={() => void klasorKaydet.yurut({ alan: 'drive', yol: driveKlasor })}>
                Drive klasörünü kaydet
              </button>
            </div>
            {klasorKaydet.hata && <UyariSerit ton="kritik">{klasorKaydet.hata}</UyariSerit>}
          </div>
        </Kart>
          </div>
        </>
      )}

      <h3 style={{ margin: 'var(--b5) 0 0', fontSize: 'var(--p-15)' }}>Yönetici araçları</h3>

      <Kart>
        <KartBaslik baslik="Yedek dosyasından geri yükle" />
        <div className="kart-govde">
          <UyariSerit ton="notr">
            Burada <strong>geri yüklenebilir yedek dosyaları</strong> listelenir:
            geri yükleme ve yazım düzeltmesi öncesinde alınan güvenlik yedekleri ile
            yerel kurulumun günlük yedekleri. Sunucudaki günlük bulut yedeği ayrı
            klasörde durur ve yukarıdaki panelden yönetilir.
          </UyariSerit>
        </div>
        {veri.yedekler.length === 0 ? (
          <BosDurum simge="yedek" baslik="Geri yüklenebilir yedek dosyası yok"
            aciklama="Geri yükleme ya da yazım düzeltmesi yapıldığında güvenlik yedeği burada belirir." />
        ) : (
          <div className="tablo-sarma">
            <table className="tablo">
              <thead><tr><th>Dosya</th><th>Tarih</th><th className="sag">Boyut</th><th /></tr></thead>
              <tbody>
                {veri.yedekler.slice(0, 20).map((y) => (
                  <tr key={y.ad}>
                    <td className="sayi" style={{ fontSize: 'var(--p-12)' }}>{y.ad}</td>
                    <td className="ikincil dar">{tarihSaatGoster(y.olusturmaAt)}</td>
                    <td className="sag ikincil">{boyutGoster(y.boyut)}</td>
                    <td className="dar">
                      <button className="btn btn-sade btn-kucuk" onClick={() => setGeriYuklenecek(y)}>
                        Geri yükle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Kart>

      <Kart>
        <KartBaslik baslik="Eski Nero verisini içe aktar" />
        <div className="kart-govde sutun">
          <UyariSerit ton="notr">
            Eski Nero Kasa uygulamasının JSON dosyasını okur: müşteriler, cari işlemler, ürünler ve
            kasa kayıtları eklenir. Mevcut kayıtlar silinmez, üzerine eklenir.
          </UyariSerit>
          <div className="satir">
            <button className="btn btn-ikincil" onClick={() => eskiDosyaRef.current?.click()}
              disabled={eskiVeri.calisiyor}>
              <Simge ad="yukle" className="simge" />
              {eskiVeri.calisiyor ? 'Aktarılıyor…' : 'JSON dosyası seç ve aktar'}
            </button>
          </div>
          <input
            ref={eskiDosyaRef} type="file" hidden accept="application/json,.json"
            onChange={(e) => {
              const d = e.target.files?.[0];
              e.target.value = '';
              if (d) void eskiVeri.yurut(d);
            }}
          />
          {eskiVeri.hata && <UyariSerit ton="kritik">{eskiVeri.hata}</UyariSerit>}
        </div>
      </Kart>

      <Kart>
        <KartBaslik baslik="Eski kayıtlarda yazımı düzelt" />
        <div className="kart-govde sutun">
          <UyariSerit ton="notr">
            Yeni girilen kayıtlar zaten düzgün yazılıyor. Bu araç DAHA ÖNCE girilmiş kayıtları
            düzeltir: “AHMET yılmaz” → “Ahmet Yılmaz”, “MOTOR YAĞI” → “Motor yağı”.
            Yalnızca harf büyüklüğü ve fazla boşluk değişir; hiçbir bilgi silinmez.
            Uygulamadan önce güvenlik yedeği alınır.
          </UyariSerit>
          <div className="satir">
            <button className="btn btn-ikincil" onClick={() => void yazimOnizle.yurut(undefined as never)}
              disabled={yazimOnizle.calisiyor || yazimUygula.calisiyor}>
              {yazimOnizle.calisiyor ? 'Taranıyor…' : 'Önce göster (hiçbir şey değişmez)'}
            </button>
            {duzeltmeOnizleme && duzeltmeOnizleme.degisen > 0 && (
              <button className="btn btn-birincil" onClick={() => void yazimUygula.yurut(undefined as never)}
                disabled={yazimUygula.calisiyor}>
                {yazimUygula.calisiyor ? 'Uygulanıyor…' : `${duzeltmeOnizleme.degisen} alanı düzelt`}
              </button>
            )}
          </div>

          {duzeltmeOnizleme && duzeltmeOnizleme.degisen === 0 && (
            <UyariSerit ton="olumlu">
              Düzeltilecek bir şey yok — {duzeltmeOnizleme.incelenen} alan tarandı, hepsi düzgün.
            </UyariSerit>
          )}

          {duzeltmeSonucu && (
            <UyariSerit ton="olumlu" baslik={`${duzeltmeSonucu.degisen} alan düzeltildi`}>
              {duzeltmeSonucu.incelenen} alan tarandı. Güvenlik yedeği: {duzeltmeSonucu.yedek}
            </UyariSerit>
          )}

          {(duzeltmeOnizleme ?? duzeltmeSonucu) && (duzeltmeOnizleme ?? duzeltmeSonucu)!.degisen > 0 && (
            <div className="tablo-sarma" style={{ maxHeight: 320 }}>
              <table className="tablo">
                <thead><tr><th>Alan</th><th>Şu an</th><th>Olacak</th></tr></thead>
                <tbody>
                  {(duzeltmeOnizleme ?? duzeltmeSonucu)!.ornekler.map((o, i) => (
                    <tr key={`${o.tablo}-${o.id}-${i}`}>
                      <td className="ikincil dar">{o.etiket}</td>
                      <td style={{ color: 'var(--metin-3)' }}>{o.onceki}</td>
                      <td className="ana">{o.sonraki}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {yazimOnizle.hata && <UyariSerit ton="kritik">{yazimOnizle.hata}</UyariSerit>}
          {yazimUygula.hata && <UyariSerit ton="kritik">{yazimUygula.hata}</UyariSerit>}
        </div>
      </Kart>

      {geriYuklenecek && (
        <OnayPenceresi
          baslik="Yedekten geri yükle"
          aciklama={
            `DİKKAT: Mevcut tüm veriler "${geriYuklenecek.ad}" yedeğiyle değiştirilecek. `
            + 'Önce otomatik olarak güvenlik yedeği alınır; yanlış dosya seçilirse ondan dönülebilir.'
          }
          onayMetni="Geri yükle"
          kapat={() => setGeriYuklenecek(null)}
          onayla={() => void geriYukle.yurut(geriYuklenecek.ad)}
          calisiyor={geriYukle.calisiyor}
        />
      )}
      {geriYukle.hata && <UyariSerit ton="kritik">{geriYukle.hata}</UyariSerit>}
    </div>
  );
}

/* ---------- Denetim kaydı ---------- */

const EYLEM_ADI: Record<string, string> = {
  olustur: 'Oluşturdu', guncelle: 'Güncelledi', sil: 'Sildi', durum: 'Durum değiştirdi',
  odeme: 'Ödeme aldı', odeme_sil: 'Ödeme sildi', kalem_ekle: 'Kalem ekledi', kalem_sil: 'Kalem sildi',
  stok_duzelt: 'Stok düzeltti', giris: 'Giriş yaptı', yedek: 'Yedek aldı', ayar: 'Ayar değiştirdi',
  geri_yukle: 'Geri yükledi', kalici_sil: 'Kalıcı sildi', sifre_degistir: 'Şifre değiştirdi',
  dosya_yukle: 'Dosya yükledi',
};

const VARLIK_ADI: Record<string, string> = {
  is_emri: 'İş emri', kasa: 'Kasa', urun: 'Ürün', musteri: 'Müşteri', arac: 'Araç',
  fiyat: 'Fiyat', hizmet: 'Hizmet', kullanici: 'Kullanıcı', oturum: 'Oturum',
  yedek: 'Yedek', ayar: 'Ayar', dosya: 'Dosya', cari_hareket: 'Cari', log_analiz: 'Log analizi',
};

function Audit() {
  const [varlik, setVarlik] = useState('');
  const { veri, yukleniyor } = useVeri<AuditKayit[]>(`/audit${sorgu({ varlik, limit: 300 })}`);

  return (
    <div className="sutun" style={{ gap: 'var(--b4)' }}>
      <div className="satir">
        <select className="secim" style={{ width: 220 }} value={varlik} onChange={(e) => setVarlik(e.target.value)}>
          <option value="">Tüm işlemler</option>
          {Object.entries(VARLIK_ADI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <Kart>
        {yukleniyor ? <div className="kart-govde"><Yukleniyor /></div>
          : !veri?.length ? <BosDurum simge="gecmis" baslik="Kayıt yok" />
          : (
            <div className="tablo-sarma">
              <table className="tablo">
                <thead><tr><th>Zaman</th><th>Kullanıcı</th><th>İşlem</th><th>Kayıt</th><th>Özet</th></tr></thead>
                <tbody>
                  {veri.map((a) => (
                    <tr key={a.id}>
                      <td className="ikincil dar">{tarihSaatGoster(a.created_at)}</td>
                      <td className="ana">{a.kullanici_adi}</td>
                      <td>
                        <Rozet ton={a.eylem.includes('sil') ? 'kritik' : a.eylem === 'olustur' ? 'olumlu' : 'notr'}>
                          {EYLEM_ADI[a.eylem] ?? a.eylem}
                        </Rozet>
                      </td>
                      <td className="ikincil">{VARLIK_ADI[a.varlik] ?? a.varlik}</td>
                      <td className="ikincil">{a.ozet || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Kart>
    </div>
  );
}

/* ---------- Çöp kutusu ---------- */

interface CopSatir { tablo: string; id: number; etiket: string; deleted_at: string }

function Cop() {
  const bildir = useBildirim();
  const { veri, yukleniyor, yenile } = useVeri<CopSatir[]>('/cop');
  const [silinecek, setSilinecek] = useState<CopSatir | null>(null);

  const geriYukle = useEylem(async (s: CopSatir) => {
    await api.gonder('/cop/geri-yukle', { tablo: s.tablo, id: s.id });
    yenile();
    bildir('Kayıt geri yüklendi');
  });
  const kaliciSil = useEylem(async (s: CopSatir) => {
    await api.gonder('/cop/kalici-sil', { tablo: s.tablo, id: s.id });
    yenile();
    setSilinecek(null);
    bildir('Kayıt kalıcı olarak silindi');
  });

  return (
    <div className="sutun" style={{ gap: 'var(--b4)' }}>
      <UyariSerit ton="notr">
        Silinen kayıtlar buraya düşer. Geri yüklenebilir; kalıcı silme geri alınamaz.
      </UyariSerit>
      <Kart>
        {yukleniyor ? <div className="kart-govde"><Yukleniyor /></div>
          : !veri?.length ? <BosDurum simge="cop" baslik="Çöp kutusu boş" />
          : (
            <div className="tablo-sarma">
              <table className="tablo">
                <thead><tr><th>Kayıt</th><th>Tür</th><th>Silinme</th><th /></tr></thead>
                <tbody>
                  {veri.map((s) => (
                    <tr key={`${s.tablo}-${s.id}`}>
                      <td className="ana">{s.etiket}</td>
                      <td className="ikincil">{VARLIK_ADI[s.tablo] ?? s.tablo}</td>
                      <td className="ikincil dar">{tarihSaatGoster(s.deleted_at)}</td>
                      <td className="dar">
                        <div className="satir">
                          <button className="btn btn-ikincil btn-kucuk" disabled={geriYukle.calisiyor}
                            onClick={() => void geriYukle.yurut(s)}>Geri yükle</button>
                          <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setSilinecek(s)} aria-label="Kalıcı sil">
                            <Simge ad="cop" boyut={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Kart>
      {silinecek && (
        <OnayPenceresi
          baslik="Kalıcı olarak sil"
          aciklama={`«${silinecek.etiket}» tamamen silinecek. Bu işlem geri alınamaz.`}
          onayMetni="Kalıcı sil"
          kapat={() => setSilinecek(null)}
          onayla={() => void kaliciSil.yurut(silinecek)}
          calisiyor={kaliciSil.calisiyor}
          hata={kaliciSil.hata}
        />
      )}
    </div>
  );
}
